/**
 * Saved views: the four doors on a named filter set, and the one comparison
 * that has to be canonical.
 *
 * Its own file rather than more lines inside `lib/projects.ts`, whose own header
 * already concedes it is at the size where the next feature has to go somewhere
 * else (AGENTS.md §4) - and this one does not belong there anyway. A saved view
 * knows nothing about a project: it is a note an operator writes about how they
 * like to look at a register, and the API deliberately keeps it
 * section-agnostic, so the task register will read these same four endpoints
 * with nothing here changing but the `section` it asks for.
 *
 * ── WHAT THE `query` IS, AND WHAT IT IS NOT ────────────────────────────────
 *
 * It is the SCREEN'S filter set - the same key/value pairs the register's
 * address bar carries - and it is applied by the screen writing it back into the
 * address bar and letting its own parser read it (`applyQuery` in
 * `crm/sections/projects/listView.ts`). It is never a where-clause and it is
 * never interpreted here: this file moves it, the register's parser decides what
 * it means, and a key neither of them recognises degrades into a broader list
 * rather than into an error. The API states the whole argument in
 * `crm-projects/lib/savedViewQuery.ts`.
 */
import { api } from "./api.js";

/** The registers that can read a saved view. The same closed list the API
 *  keeps: a view is only ever offered on the screen that can apply it, and a
 *  projects filter listed on the task register would apply nothing and leave the
 *  operator reading a full list they believe is filtered. */
export type SavedViewSection = "projects" | "tasks";

/** What a filter set may hold: what a query string can carry, and no more. The
 *  API sanitises to exactly this on both sides of storage, so anything richer
 *  that reaches a screen has already been dropped. */
export type SavedViewScalar = string | number | boolean;
export type SavedViewValue = SavedViewScalar | SavedViewScalar[];
export type SavedViewQuery = Record<string, SavedViewValue>;

/** One saved view, as the API hands it back. */
export interface SavedView {
  id: string;
  section: string;
  name: string;
  query: SavedViewQuery;
  /** Whether colleagues see it in their own list. */
  shared: boolean;
  /** The stored position within the owner's list. The server sorts on it and
   *  breaks ties on `createdAt` and `id`, so a screen draws the rows in the
   *  order they ARRIVED rather than sorting them again - every row of an
   *  untouched list carries 0, and a local sort on this field alone would
   *  shuffle them between two renders with nobody having touched anything. */
  sortOrder: number;
  /**
   * Whether the signed-in operator owns this one.
   *
   * The API sends this fact instead of `ownerId`, and the difference is the
   * whole reason the field exists: `ownerId` is a PLATFORM `User.id` and the CRM
   * database cannot join it to a name, so a client holding it could only print
   * an identifier. What a menu actually needs to know is this - because the
   * writes are "mine only". A view somebody else shared can be applied and
   * nothing more, and a screen that offers Delete on one is a screen that draws
   * an action the server refuses, which teaches operators the app is broken.
   */
  mine: boolean;
  createdAt: string;
  updatedAt: string;
}

/** Mine plus everything shared, for one register, in the order the server keeps
 *  them. Never paged: the cap is 200 rows and a sidebar that long has stopped
 *  being a sidebar. */
export const getSavedViews = (section: SavedViewSection) =>
  api.get<{ rows: SavedView[] }>(`/admin/crm/saved-views?section=${encodeURIComponent(section)}`);

/**
 * Save one.
 *
 * Saving the SAME filter set under the SAME name twice answers with the row that
 * already exists rather than with a second one, so a double-clicked save button
 * cannot leave two entries an operator cannot tell apart. That is the server's
 * doing and not this caller's: the stored copy has been through jsonb, whose key
 * order is not the one it went in with, so the comparison has to happen where
 * both sides can be canonicalised against the stored row.
 */
export const createSavedView = (body: {
  section: SavedViewSection;
  name: string;
  query: SavedViewQuery;
  shared: boolean;
}) => api.post<SavedView>("/admin/crm/saved-views", body);

/** Rename, re-filter, share or reorder. Every field optional, and the server
 *  writes only what actually differs - a PATCH that repeats the stored values
 *  changes nothing and does not move `updatedAt`. Refused with 403 or 404 for a
 *  view the operator does not own. */
export const updateSavedView = (
  id: string,
  body: Partial<{ name: string; query: SavedViewQuery; shared: boolean; sortOrder: number }>,
) => api.patch<SavedView>(`/admin/crm/saved-views/${encodeURIComponent(id)}`, body);

/** Remove one. Owner only, and for a shared view this is the last place the
 *  filter set exists - colleagues had it in their sidebars. */
export const deleteSavedView = (id: string) =>
  api.del<{ ok: true }>(`/admin/crm/saved-views/${encodeURIComponent(id)}`);

/**
 * Stable serialisation: object keys sorted, `undefined` members dropped, array
 * order preserved.
 *
 * Every comparison of two filter sets goes through this, and the reason is
 * Postgres rather than JavaScript. `CrmSavedView.query` is `jsonb`, which does
 * not preserve key order, so the object that comes back out is not the object
 * that went in and a plain `JSON.stringify` comparison calls two identical
 * filter sets different. On this side of the wire the symptom is quiet and
 * specific: the menu says no view is active while the operator is looking
 * straight at one, and the next thing they do is save a duplicate.
 *
 * The third copy of these eight lines, and each copy has a reason that outweighs
 * sharing them. `crm/sections/marketing/lib.ts` has the first (`canon`, for
 * audiences) and was where the bug was found; `crm-projects/lib/savedViewQuery.ts`
 * has the second, because an API route cannot import from `apps/web_crm`.
 * Promoting them into a shared package would be a bigger change than the thing
 * being shared, and would put a browser-side helper in the path of the server's
 * duplicate check.
 *
 * Array order is KEPT, the same way it is in the other two: in a filter set a
 * list is a choice the operator made, and re-ordering it would hide a real
 * difference.
 */
export function canonQuery(value: unknown): string {
  return JSON.stringify(value, (_key, val: unknown) => {
    if (!val || typeof val !== "object" || Array.isArray(val)) return val;
    return Object.fromEntries(
      Object.entries(val as Record<string, unknown>)
        .filter(([, v]) => v !== undefined)
        .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0)),
    );
  });
}
